import { PERMISSIONS } from '@conversed/shared/src/central/constants/permissions';
import { LLMBlocksFilterSchema } from '@conversed/shared/src/central/types/blocks/LLMBlock';
import { z } from 'zod';
import { createRESTContract } from '../../index';
import { HotTopicWithRelatedSchema } from '../platform-settings/platform-settings.schema';
import { CheckResultSchema } from './check-results';
import { ReportIssuePriority } from './report-issue';
import { SortOption } from './sort-option.enum';

export const DEFAULT_SAVE_ANSWER_AS_TEMPLATE = false;
export const DEFAULT_USE_FOR_AI_KNOWLEDGE_DATA = true;
export const DEFAULT_IS_PRIORITY_KNOWLEDGE = false;
export const DEFAULT_REPORT_ISSUE = false;
export const DEFAULT_REPORT_ISSUE_PARAMS = {
  categories: [],
  priority: ReportIssuePriority.NEEDS_ATTENTION,
  comment: '',
  attachments: [],
};

export enum ConversationsViewType {
  SINGLE = 'single',
  GROUP = 'group',
}

export type TLLMQuestionAnswerPair = {
  _id: string;
  question: string;
  answer: string;
};

export const LLMUsagesHistory = z.object({
  _id: z.string(),
  userId: z.string(),
  createdAt: z.string(),
  question: z.string(),
  answer: z.string(),
  url: z.string(),
  categories: z.string(),
  keywords: z.array(z.string()).optional(),
  articleIds: z.string(),
  configId: z.string(),
  checkResult: CheckResultSchema.omit({ id: true }),
  numberOfButtonClick: z.number(),
});

export type TLLMUsagesHistory = z.infer<typeof LLMUsagesHistory>;

const LLMUsagesHistoryResponseSchema = z.object({
  histories: LLMUsagesHistory.array(),
  startDate: z.string(),
  endDate: z.string(),
  companyName: z.string(),
  totalRecords: z.number(),
});

export type TLLMUsagesHistoryResponse = z.infer<
  typeof LLMUsagesHistoryResponseSchema
>;

const LLMUsagesHistoryRequestBodySchema = z.object({
  startDate: z.string(),
  endDate: z.string(),
  configId: z.string(),
  page: z.string().optional(),
  perPage: z.string().optional(),
});

export type TLLMUsagesHistoryRequestBody = z.infer<
  typeof LLMUsagesHistoryRequestBodySchema
>;

const WeeklyOverviewQuerySchema = z.object({
  configId: z.string(),
  llmBlockId: z.string().optional(),
  year: z.string(),
  page: z.string(),
  perPage: z.string(),
  shouldReturnLLMBlocks: z.string().optional(),
  hotTopicId: z.string().optional(),
  shouldReturnHotTopics: z.string().optional(),
});

export type TWeeklyOverviewQuery = z.infer<typeof WeeklyOverviewQuerySchema>;

export const WeeklyOverviewWeekDataSchema = z.object({
  totalItems: z.number(),
  checkedItems: z.number(),
  weekNumber: z.number(),
  weekString: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  startDateForView: z.string().optional(),
  endDateForView: z.string().optional(),
});

const WeeklyOverviewResponseSchema = z.object({
  results: WeeklyOverviewWeekDataSchema.array(),
  totalRecords: z.number(),
  llmBlocks: LLMBlocksFilterSchema.array().optional(), // Include LLM blocks when platform feature is enabled
  predefinedHotTopics: z
    .object({ id: z.string(), name: z.string() })
    .array()
    .optional(), // Include predefined hot topics when HotTopicFilterForTrainAI flag is enabled
});

export type TWeeklyOverviewResponse = z.infer<
  typeof WeeklyOverviewResponseSchema
>;

export type TWeeklyOverviewWeekData = z.infer<
  typeof WeeklyOverviewWeekDataSchema
>;

const WeeklyStatisticsQuerySchema = z.object({
  configId: z.string(),
  year: z.string(),
  week: z.string(),
  llmBlockId: z.string().optional(),
});

export type TWeeklyStatisticsQuery = z.infer<
  typeof WeeklyStatisticsQuerySchema
>;

const WeeklyStatisticsResponseSchema = z.object({
  goodAnswers: z.number(),
  needsImprovementAnswers: z.number(),
  irrelevantAnswers: z.number(),
  notEvaluatedAnswers: z.number(),
  alreadyEvaluatedAnswers: z.object({
    good: z.number(),
    needsImprovement: z.number(),
    irrelevant: z.number(),
  }),
  totalAnswers: z.number(),
  weekNumber: z.number(),
});

const OverallStatisticsResponseSchema = WeeklyStatisticsResponseSchema.omit({
  weekNumber: true,
});

export type TWeeklyStatisticsResponse = z.infer<
  typeof WeeklyStatisticsResponseSchema
>;

export type OverallStatisticsResponse = z.infer<
  typeof OverallStatisticsResponseSchema
>;

export enum FactCheckingAnswersFilterOptions {
  ONLY_UNVERIFIED = 'only-unverified',
  ALL = 'all',
}

const ConversationsQuerySchema = z.object({
  configId: z.string(),
  weeks: z.string(),
  year: z.string(),
  llmBlockId: z.string().optional(),
  page: z.string().optional(),
  perPage: z.string().optional(),
  onlyUnverifiedAnswers: z.string(),
  sortOption: z.enum(
    Object.values(SortOption) as [SortOption, ...SortOption[]],
  ),
  includeNotEvaluable: z.string(),
  viewType: z.enum(
    Object.values(ConversationsViewType) as [
      ConversationsViewType,
      ...ConversationsViewType[],
    ],
  ),
  // Comma-separated list of keywords to filter by; omit or leave empty for no keyword filter
  keywords: z.string().optional(),
  hotTopicId: z.string().optional(),
});

export type TConversationsQuery = z.infer<typeof ConversationsQuerySchema>;
export type TConversationDetailsSchema = z.infer<
  typeof ConversationDetailsSchema
>;

const GroupMetadataSchema = z.object({
  isConversationGroup: z.boolean(),
  messageCount: z.number(),
  userId: z.string(),
});

export type TGroupMetadata = z.infer<typeof GroupMetadataSchema>;

const EvaluatedInSchema = z.object({
  configId: z.string(),
  llmBlockId: z.string(),
  isEvaluable: z.boolean(),
});

export type TEvaluatedIn = z.infer<typeof EvaluatedInSchema>;

const ArticleSchema = z.object({
  id: z.string().optional(),
  content: z.string().optional(),
  url: z.string().optional(),
  category: z.string().optional(),
  keywords: z.union([z.string(), z.array(z.string())]).optional(),
  chunk: z.string().optional(),
  score: z.number().optional(),
  isTopArticle: z.boolean().optional(),
  sourceName: z.string().optional(),
  label: z.string().optional(),
  createdAt: z.date().optional(),
});

export type TArticle = z.infer<typeof ArticleSchema>;

export const LLMUsageTranslationSchema = z.object({
  question: z.string(),
  answer: z.string(),
  sourceLang: z.string(),
});

export type TLLMUsageTranslation = z.infer<typeof LLMUsageTranslationSchema>;

const ConversationDetailsSchema = z.object({
  _id: z.string(),
  userId: z.string(),
  createdAt: z.string(),
  question: z.string(),
  answer: z.string(),
  url: z.string(),
  categories: z.string(),
  keywords: z.array(z.string()).optional(),
  articleIds: z.string(),
  articles: ArticleSchema.array().optional(),
  topMatchScore: z.number().optional(),
  configId: z.string(),
  checkResult: CheckResultSchema.omit({ id: true }).optional(),
  week: z.number(),
  isEvaluable: z.boolean(),
  groupMetadata: GroupMetadataSchema.optional(),
  hotTopicMetadata: HotTopicWithRelatedSchema.optional(),
  evaluatedIn: z.array(EvaluatedInSchema).optional(),
  llmBlockId: z.string().optional(),
  label: z.string().optional(),
  translation: LLMUsageTranslationSchema.optional(),
  isIntentRedirect: z.boolean().optional(),
});

export const ConversationsResponseSchema = z.object({
  results: ConversationDetailsSchema.array(),
  totalRecords: z.number(),
  totalCheckedItems: z.number(),
  totalCheckableItems: z.number(),
  // "totalCheckableItems" - The total number of individual LLM questions that can be checked in the selected period.
  // If the view type is "Single", this equals totalRecords; however, if the view type is "Group", these values differ.
  // We need this value for the progress indicator on the Conversation Checklist pages.
});

export type TConversationsResponse = z.infer<
  typeof ConversationsResponseSchema
>;

export const CONVERSATIONS_DEFAULT_STATE = {
  results: [],
  totalRecords: 0,
  totalCheckedItems: 0,
  totalCheckableItems: 0,
};

const DuplicateQuestionResponseSchema = z.object({
  question: z.string(),
  indexes: z
    .object({
      week: z.number(),
      index: z.number(),
      id: z.string(),
      details: ConversationDetailsSchema,
    })
    .array(),
});

const DataSourceGenerationResponseSchema = z.object({
  factCheckedData: ConversationDetailsSchema.array(),
  duplicateQuestions: DuplicateQuestionResponseSchema.array(),
  hyperlinksCorrectedCount: z.number(),
});

export type TDataSourceGenerationResponse = z.infer<
  typeof DataSourceGenerationResponseSchema
>;

const DataSourceGenerationQuerySchema = z.object({
  configId: z.string(),
  llmBlockId: z.string().optional(),
  weeks: z.string(),
  year: z.string(),
  hotTopicId: z.string().optional(),
});

export type TDataSourceGenerationQuery = z.infer<
  typeof DataSourceGenerationQuerySchema
>;

const ConversationsByIdsQuerySchema = z.object({
  ids: z.string().array(),
});

export type TConversationsByIdsQuery = z.infer<
  typeof ConversationsByIdsQuerySchema
>;

export type TPeriodicQuestionsQuery = {
  startDate: string;
  endDate: string;
};

export type TPeriodicQuestionHotTopicEntry = {
  _id: string;
  prompt: string;
  answer?: string;
};

const TranslateConversationBodySchema = z.object({
  question: z.string(),
  answer: z.string(),
});

export type TTranslateConversationBody = z.infer<
  typeof TranslateConversationBodySchema
>;

const TranslateConversationResponseSchema = z.object({
  sourceLanguageName: z.string(),
  sourceLang: z.string(),
  translatedQuestion: z.string(),
  translatedAnswer: z.string(),
});

export type TTranslateConversationResponse = z.infer<
  typeof TranslateConversationResponseSchema
>;

export const llmUsagesContract = createRESTContract({
  getLLMUsagesHistory: {
    method: 'GET',
    path: '/llm-usages/history',
    permissions: [],
    response: LLMUsagesHistoryResponseSchema,
    query: LLMUsagesHistoryRequestBodySchema,
  },
  updateCheckResult: {
    method: 'POST',
    path: '/llm-usages/check-result',
    permissions: [PERMISSIONS.TRAINER_AI.TRAINER_AI_EDIT],
    body: CheckResultSchema,
    response: CheckResultSchema,
  },
  bulkUpdateCheckResult: {
    method: 'POST',
    path: '/llm-usages/bulk-check-result',
    permissions: [PERMISSIONS.TRAINER_AI.TRAINER_AI_EDIT],
    body: z.array(CheckResultSchema),
    response: z.array(CheckResultSchema),
  },
  getWeeklyOverview: {
    method: 'GET',
    path: '/llm-usages/weekly-overview',
    permissions: [PERMISSIONS.TRAINER_AI.TRAINER_AI_VIEW],
    response: WeeklyOverviewResponseSchema,
    query: WeeklyOverviewQuerySchema,
  },
  getWeeklyStatistics: {
    method: 'GET',
    path: '/llm-usages/weekly-statistics',
    permissions: [PERMISSIONS.TRAINER_AI.TRAINER_AI_VIEW],
    response: WeeklyStatisticsResponseSchema,
    query: WeeklyStatisticsQuerySchema,
  },
  getConversationsByWeeks: {
    method: 'GET',
    path: '/llm-usages/conversations/weeks',
    permissions: [PERMISSIONS.TRAINER_AI.TRAINER_AI_CHECKLIST],
    response: ConversationsResponseSchema,
    query: ConversationsQuerySchema,
  },
  getDataForDataSourceGeneration: {
    method: 'GET',
    path: '/llm-usages/ai-knowledge-source-generation',
    permissions: [PERMISSIONS.TRAINER_AI.TRAINER_AI_EDIT],
    response: DataSourceGenerationResponseSchema,
    query: DataSourceGenerationQuerySchema,
  },
  getConversationsByIds: {
    method: 'POST',
    path: '/llm-usages/conversations/ids',
    permissions: [PERMISSIONS.TRAINER_AI.TRAINER_AI_CHECKLIST],
    response: ConversationsResponseSchema,
    body: ConversationsByIdsQuerySchema,
  },
  translateConversation: {
    method: 'POST',
    path: '/llm-usages/:id/translate',
    permissions: [PERMISSIONS.TRAINER_AI.TRAINER_AI_CHECKLIST],
    body: TranslateConversationBodySchema,
    response: TranslateConversationResponseSchema,
  },
});
